// src\components\buscador\components\FiltrosPanel.jsx
import React from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  MenuItem,
  IconButton,
  Switch,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

const FiltrosPanel = ({
  isMobile,
  onCloseMobile,

  search, setSearch,
  usuario, setUsuario,
  fechaDesde, setFechaDesde,
  fechaHasta, setFechaHasta,
  nivelId, setNivelId,
  gradoId, setGradoId,
  areaId, setAreaId,

  tipoSesion, setTipoSesion,
  estado, setEstado,
  regionId, setRegionId,
  provinciaId, setProvinciaId,
  distritoId, setDistritoId,

  ieId, setIeId,
  instituciones = [],

  vistaDistrital,
  setVistaDistrital,

  usuarios,
  showAuthorFilter = false,
  niveles,
  grados,
  areas,
  regiones = [],
  provincias = [],
  distritos = [],

  ieUsuarioId = "",
  nombreIeUsuario = "",

  onBuscar,
  onLimpiar,

  userRole,
  maxPanelHeight,
  panelHeight,
}) => {
  // El Admin y el Director pueden filtrar por autor
  const canFilterByAuthor = userRole === "ADMIN" || userRole === "DIRECTOR";
  const isAdmin = userRole === "ADMIN";
  const canFilterSAE = isAdmin || vistaDistrital;
  const tipoSesionOptions = [
    { value: "SAC", label: "SAC" },
    ...(canFilterSAE ? [{ value: "SAE", label: "SAE" }] : []),
  ];
  const shouldShowTipoSesion = tipoSesionOptions.length > 1;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onBuscar();
  };

  const handleToggleVistaDistrital = (checked) => {
    setVistaDistrital(checked);
    setIeId("");

    const nextTipoSesion = !checked && tipoSesion === "SAE" ? "" : tipoSesion;
    if (nextTipoSesion !== tipoSesion) {
      setTipoSesion(nextTipoSesion);
    }

    onBuscar(
      {
        vista_distrital: checked,
        id_institucion_edu: "",
        tipo_sesion: nextTipoSesion,
      },
      { closeDrawer: false }
    );
  };
  return (
    <Box
      component="form"
      onSubmit={handleFormSubmit}
      className="filtros-panel"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: panelHeight || "100%",
        maxHeight: maxPanelHeight || "none",
        minHeight: 0,
        overflowX: "hidden",
      }}
    >

      {/* CABECERA DEL PANEL */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: .5 }}>
        <Typography variant="h6" className="titulo-filtros" sx={{ fontWeight: 700, m: 0 }}>
          Filtros de Búsqueda
        </Typography>
        {isMobile && (
          <IconButton onClick={onCloseMobile} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>

        {/* --- TOGGLE VISTA DISTRITAL --- */}
        {!isAdmin && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: "#f8fafc",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
              <Switch
                checked={vistaDistrital}
                onChange={(e, checked) => handleToggleVistaDistrital(checked)}
                sx={{ mt: 0.25, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "primary.main",
                }}
              >
                Búsqueda ampliada 
              </Typography>
            </Stack>

            <Typography variant="caption" color="text.secondary" >
              Permite buscar sesiones de otros colegios dentro de tu distrito.
            </Typography>
          </Box>
        )}

        {/* BÚSQUEDA GENERAL */}
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          Búsqueda por Tema o Descripción
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Ej: Ecosistemas, Álgebra..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, ms: 0 }}
        />


        {/* TIPO Y ESTADO DE SESIÓN */}
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          Clasificación
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          {shouldShowTipoSesion && (
            <TextField
              select
              fullWidth
              size="small"
              label="Tipo"
              value={tipoSesion}
              onChange={(e) => setTipoSesion(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {tipoSesionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            fullWidth
            size="small"
            label="Estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            <MenuItem value="guardado">En proceso</MenuItem>
            <MenuItem value="producido">Generado</MenuItem>
          </TextField>
        </Box>


        {/* UBICACIÓN GEOGRÁFICA (SOLO ADMIN) */}
        {isAdmin && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
              Ubicación Geográfica
            </Typography>

            <TextField
              select
              fullWidth
              size="small"
              label="Región"
              value={regionId}
              onChange={(e) => setRegionId(e.target.value)}
              sx={{ mb: 1.5 }}
            >
              <MenuItem value=""><em>Todas</em></MenuItem>
              {regiones.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.nombre}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Provincia"
              value={provinciaId}
              onChange={(e) => setProvinciaId(e.target.value)}
              disabled={!regionId}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={{ mb: 1.5 }}
            >
              <MenuItem value=""><em>Todas</em></MenuItem>
              {provincias.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.nombre}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Distrito"
              value={distritoId}
              onChange={(e) => setDistritoId(e.target.value)}
              disabled={!provinciaId}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={{ mb: 1.5 }}
            >
              <MenuItem value=""><em>Todos</em></MenuItem>
              {distritos.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.nombre}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* INSTITUCIÓN EDUCATIVA (ADMIN O VISTA DISTRITAL) */}
        {(isAdmin || vistaDistrital) && (
          <Box sx={{ mb: 2 }}>
            {!isAdmin && (
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                Filtrar por Colegio
              </Typography>
            )}

            <TextField
              select
              fullWidth
              size="small"
              label="Institución Educativa"
              value={ieId || ""}
              onChange={(e) => setIeId(e.target.value)}
              disabled={isAdmin ? !distritoId : false}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={{ mb: 1.5 }}
            >
              <MenuItem value=""><em>Todos</em></MenuItem>
              {/* {instituciones && instituciones.map((opt) => {
                const isUserInstitution =
                  vistaDistrital &&
                  String(opt.id) === String(ieUsuarioId);

                return (
                  <MenuItem key={opt.id} value={opt.id}>
                    {isUserInstitution
                      ? `Mi institución educativa`
                      :
                      `${opt.nombre}${opt.nivel_modalidad ? ` (${opt.nivel_modalidad})` : ""}`}
                  </MenuItem>
                );
              })} */}
              {instituciones && instituciones.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.nombre} {opt.nivel_modalidad ? `(${opt.nivel_modalidad})` : ""}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* PARÁMETROS CURRICULARES */}
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          Parámetros Curriculares
        </Typography>
        {[
          {
            label: "Nivel educativo",
            value: nivelId,
            onChange: setNivelId,
            options: niveles,
            getLabel: (n) => n.nombre,
            disabled: false,
            keyPrefix: 'nivel'
          },
          {
            label: "Grado",
            value: gradoId,
            onChange: setGradoId,
            options: grados,
            getLabel: (g) => g.descripcion,
            disabled: !nivelId,
            keyPrefix: 'grado'
          },
          {
            label: "Área temática",
            value: areaId,
            onChange: setAreaId,
            options: areas,
            getLabel: (a) => a.nombre,
            disabled: !gradoId,
            keyPrefix: 'area'
          },
        ].map((f, idx) => (
          <TextField
            key={idx}
            select
            fullWidth
            size="small"
            label={f.label}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            disabled={f.disabled}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {f.options && f.options.map((opt) => (
              <MenuItem key={`${f.keyPrefix}-${opt.id}`} value={opt.id}>
                {f.getLabel(opt)}
              </MenuItem>
            ))}
          </TextField>
        ))}


        {/* AUTORÍA Y FECHAS */}
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          {canFilterByAuthor && showAuthorFilter ? "Autoría y Rango de Creación" : "Rango de Creación"}
        </Typography>

        {canFilterByAuthor && showAuthorFilter && (
          <TextField
            select
            fullWidth
            size="small"
            label="Docente responsable"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value=""><em>Cualquier autor</em></MenuItem>
            {usuarios.map((u) => (
              <MenuItem key={`user-${u.id}`} value={u.id}>
                {u.nombres}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Desde"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Hasta"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </Box>
      </Box>

      {/* BOTONES DE ACCIÓN */}
      <Box sx={{ display: "flex", gap: 1, pt: 1, borderTop: "1px solid #c7c7c7" }}>
        <Button
          type="button"
          variant="outlined"
          fullWidth
          size="medium"
          onClick={onLimpiar}
          className="btn-limpiar"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Limpiar
        </Button>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="medium"
          className="btn-buscar"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Aplicar filtros
        </Button>
      </Box>

    </Box>
  );
};

export default FiltrosPanel;
